/**
 * ผูกแล้ว / Done — the per-row control on a claim-import run page.
 *
 * ⚠️ THE SERVER DECIDES WHICH ACT IS LEGAL, NOT THIS COMPONENT. A link can mean four
 * different things to billing — write รับแล้ว, correct a date, write nothing at all, or
 * record รับบางส่วน — and which one applies depends on a LIVE read of the billing line that
 * only the server has. So the button asks, and the server answers with either a result or a
 * 409 saying what is still needed. Nothing here guesses, and nothing here names an amount
 * the server did not ask for.
 *
 * ⚠️ THE TWO 409s ARE THE FEATURE, NOT ERRORS TO SWALLOW:
 *   needs: 'partial'   the payer paid LESS. The operator must type the real figure —
 *                      owner's rule: a row cannot be Done while money is unexplained.
 *   file_exceeds_crm   the payer paid MORE than we billed. api 631 cannot express that,
 *                      so the row stays not-Done and we point at ขอแก้ยอดเบิก instead of
 *                      inventing a state for it.
 *
 * ⚠️ NEVER window.confirm — house rule. Unlink goes through ConfirmDialog.
 */
import { useState } from 'react'
import { Link2, Link2Off, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react'
import ConfirmDialog from '../ConfirmDialog.jsx'
import { postJson } from '../../lib/api.js'
import { apiErrorFromBody } from '../../lib/apiError.js'

const fmt = (n) => Number(n || 0).toLocaleString('th-TH', { maximumFractionDigits: 2 })

/**
 * ⚠️ A ROW CELL IS NOT A PLACE TO RENDER A RESPONSE BODY. The first real click hit a 500 and
 * this rendered Cloudflare's entire styled HTML error document into the table — thousands of
 * characters of markup pushed down the page. The server side is fixed so it answers JSON
 * now, but the UI must not depend on that: anything that is not a short human sentence gets
 * replaced by one, and the detail stays in the console for whoever is debugging.
 */
function shortError(body, e) {
  const raw = apiErrorFromBody(body, e?.status, String(e?.message || ''))
  if (raw) console.error('[LinkControl]', raw)        // eslint-disable-line no-console
  const looksLikeMarkup = /<[a-z!/]/i.test(raw)
  if (!raw || looksLikeMarkup || raw.length > 200) {
    return 'ผูกไม่สำเร็จ — ระบบขัดข้อง ลองใหม่อีกครั้ง หรือแจ้งผู้ดูแลพร้อมเลขรายการนี้'
  }
  return raw
}

/** What the ผูกแล้ว chip says. The kind matters: three of them moved money, one did not. */
const KIND_LABEL = {
  received: 'ผูกแล้ว · บันทึกรับเงิน',
  date_fix: 'ผูกแล้ว · แก้วันที่',
  already_received: 'ผูกแล้ว · รับอยู่ก่อนแล้ว',
  partial: 'ผูกแล้ว · รับบางส่วน',
  manual: 'ผูกแล้ว · ผูกเอง',
}

export default function LinkControl({ runId, row, mayLink, bulkHandles, liveStatus, liveAmount, liveReceived, toleranceSatang = 0, onDone }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [partial, setPartial] = useState(null)   // { file_amount, crm_amount, value }
  const [manualId, setManualId] = useState('')
  const [confirmUnlink, setConfirmUnlink] = useState(false)

  const state = String(row.link_state || '')
  const linked = state === 'linked'
  const dismissed = state === 'dismissed'
  /* ⚠️ A row committed through the OLD batch path carries write_confirmed and no link_state,
     and those are exactly the rows sitting in บันทึกแล้ว today. The money moved, so the only
     honest control is ยกเลิก — offering ผูก there would invite writing `received` onto a line
     that already says `received`. Judged from the LIVE billing status, not from link_state. */
  const liveMoved = ['received', 'partial'].includes(String(liveStatus || '').toLowerCase())
  const undoOnly = !linked && !dismissed && liveMoved && row.write_confirmed === true

  async function call(payload) {
    setBusy(true); setErr(null)
    try {
      const r = await postJson(`/api/claim-import/run/${runId}/link`, { row_id: row.id, ...payload })
      setPartial(null); setManualId('')
      onDone?.(r)
    } catch (e) {
      const b = e?.body || {}
      // The server telling us what it still needs, not a failure.
      if (b.needs === 'partial') {
        setPartial({ file_amount: b.file_amount, crm_amount: b.crm_amount, value: String(b.file_amount ?? '') })
      } else {
        setErr(shortError(b, e))
      }
    } finally { setBusy(false) }
  }

  if (dismissed) {
    return <span className="text-[11.5px] text-slate-500">ไม่ต้องผูก{row.note ? ` · ${row.note}` : ''}</span>
  }

  if (linked) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] bg-emerald-100 text-emerald-800">
          <CheckCircle2 size={11} /> {KIND_LABEL[row.link_kind] || 'ผูกแล้ว'}
        </span>
        {Number(row.received_amount_at_link) > 0 && row.link_kind === 'partial' ? (
          <span className="text-[11px] text-amber-800 tabular-nums">รับจริง {fmt(row.received_amount_at_link)} ฿</span>
        ) : null}
        {mayLink ? (
          <button type="button" disabled={busy} onClick={() => setConfirmUnlink(true)}
            className="inline-flex items-center gap-1 text-[11.5px] text-slate-500 hover:text-rose-600 disabled:opacity-40">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Link2Off size={11} />} ยกเลิกการผูก
          </button>
        ) : null}
        {err ? <span className="text-[11px] text-rose-700">{err}</span> : null}

        {confirmUnlink ? (
          <ConfirmDialog
            tone="danger"
            title="ยกเลิกการผูก?"
            message={
              row.link_kind === 'already_received'
                ? 'รอบนี้ไม่ได้เขียนอะไรลงบิล การยกเลิกจะลบเฉพาะการผูก บิลไม่ถูกแตะต้อง'
                : row.link_kind === 'date_fix'
                  ? 'จะคืนวันที่เงินเข้าเป็นค่าเดิม บิลยังเป็นรับแล้วเหมือนเดิม ไม่ถูกเปลี่ยนเป็นค้างรับ'
                  : row.link_kind === 'partial'
                    ? 'จะเปิดบิลกลับเป็นค้างรับ และล้างยอดรับบางส่วนทิ้ง'
                    : 'จะเปลี่ยนบิลกลับเป็นค้างรับ'
            }
            confirmLabel="ยกเลิกการผูก"
            onConfirm={() => { setConfirmUnlink(false); call({ action: 'unlink' }) }}
            onCancel={() => setConfirmUnlink(false)}
          />
        ) : null}
      </div>
    )
  }

  /* ⚠️ NO PER-ROW BUTTON WHERE THE TICK BOX ALREADY DOES THE JOB. ตรวจสอบและยืนยัน links in
     bulk, so on a staged run a matched row whose amounts agree needs no second control —
     two ways to do one thing on the same line is what made the old screen confusing.
     The button therefore appears only where bulk CANNOT act: จับคู่ไม่ได้ (needs a billing
     id), กำกวม, ยอดไม่ตรง (needs a per-row decision), and — the case that rescues stranded
     money — a leftover row on a run that is already closed, where the one-shot bulk commit
     can never run again. */
  if (undoOnly) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {mayLink ? (
          <button type="button" disabled={busy} onClick={() => setConfirmUnlink(true)}
            className="inline-flex items-center gap-1 h-7 px-2 rounded border border-slate-300 bg-white text-[11.5px] text-slate-600 hover:border-rose-300 hover:text-rose-600 disabled:opacity-40">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Link2Off size={11} />} ยกเลิก
          </button>
        ) : null}
        {err ? <span className="text-[11px] text-rose-700">{err}</span> : null}
        {confirmUnlink ? (
          <ConfirmDialog
            tone="danger"
            title="ยกเลิกรายการนี้?"
            message="จะเปลี่ยนสถานะตามเบิกของบิลใบนี้กลับเป็น ค้างรับ"
            confirmLabel="ยกเลิก"
            onConfirm={() => { setConfirmUnlink(false); call({ action: 'unlink' }) }}
            onCancel={() => setConfirmUnlink(false)}
          />
        ) : null}
      </div>
    )
  }

  if (bulkHandles) return null

  if (!mayLink) return <span className="text-[11.5px] text-slate-400">ยังไม่ผูก</span>

  /* ── the partial prompt: the server asked for a real figure ─────────────── */
  if (partial) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11.5px] text-amber-900">
        <div className="mb-1.5">
          ยอดไม่ตรงกัน — <b className="tabular-nums">ไฟล์ {fmt(partial.file_amount)}</b>
          {' · '}<span className="tabular-nums">ระบบ {fmt(partial.crm_amount)}</span>
        </div>
        {/* ⚠️ BOTH options are shown, and the unavailable one says why. Offering only
            รับบางส่วน would leave the operator wondering whether รับแล้ว was forgotten;
            showing it greyed out with the reason is what makes the rule legible. */}
        <div className="mb-1.5 flex flex-col gap-0.5">
          <span className="text-slate-500">
            ○ รับแล้ว — <b>เลือกไม่ได้</b> เพราะยอดสองฝั่งไม่ตรงกัน ต้องแก้ยอดเบิกให้ตรงก่อน
          </span>
          <span>● รับบางส่วน — กรอกยอดที่ได้รับจริง แล้วผูกได้เลย</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            value={partial.value}
            onChange={(e) => setPartial((p) => ({ ...p, value: e.target.value }))}
            inputMode="decimal" placeholder="ยอดที่ได้รับจริง"
            className="h-7 w-36 rounded border border-amber-300 px-1.5 text-[11.5px] tabular-nums"
          />
          <button type="button" disabled={busy || !(Number(partial.value) > 0)}
            onClick={() => call({ action: 'link', received_amount: Number(partial.value) })}
            className="h-7 px-2 rounded bg-amber-600 text-white text-[11.5px] hover:bg-amber-700 disabled:opacity-40">
            {busy ? <Loader2 size={11} className="animate-spin inline" /> : null} ผูกเป็นรับบางส่วน
          </button>
          <button type="button" onClick={() => setPartial(null)}
            className="h-7 px-2 text-[11.5px] text-slate-600 hover:underline">ยกเลิก</button>
        </div>
        {err ? <div className="mt-1 text-rose-700">{err}</div> : null}
      </div>
    )
  }

  /* ── unmatched: the operator supplies the line. The SERVER still checks it is
        inside this run's own /sso/claims window — see link.js. ─────────────── */
  const needsManual = !Number(row.billing_id)

  /* ⚠️ AN OVERPAYMENT HAS NO ผูก PATH AT ALL, so it must not be offered one. api 631 requires
     received_amount < priceadvance, so `partial` structurally cannot express "the payer sent
     MORE than we billed" — link.js refuses these with file_exceeds_crm and always will. The
     button was still rendered, so the only thing pressing it could ever produce was a red
     error. The sole way forward is ขอแก้ยอดเบิก, which sits on the same row.
     ⚠️ Judged from LIVE billing, never from `outcome`. `outcome` is the matcher's verdict
     frozen at import; the whole point is that this control must disappear by itself the
     moment an approved ขอแก้ยอดเบิก makes the two figures agree. Owner, 2026-09-15. */
  const sat = (v) => Math.round((Number(v) || 0) * 100)
  const outstanding = sat(liveAmount) - sat(liveReceived)
  const overpaid = Number.isFinite(outstanding) && outstanding > 0
    && sat(row.file_amount_paid) - outstanding > (Number(toleranceSatang) || 0)

  if (overpaid && !needsManual) {
    return (
      <div className="text-[11px] text-rose-700 max-w-[380px]">
        ไฟล์จ่ายมากกว่ายอดที่เรียกเก็บ — ผูกไม่ได้จนกว่าจะแก้ยอดเบิกให้ตรงกันก่อน
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {needsManual ? (
        <input
          value={manualId}
          onChange={(e) => setManualId(e.target.value.replace(/[^\d]/g, ''))}
          inputMode="numeric" placeholder="เลขที่รายการตามเบิก"
          className="h-7 w-40 rounded border border-slate-300 px-1.5 text-[11.5px] tabular-nums"
        />
      ) : null}
      <button
        type="button"
        disabled={busy || (needsManual && !manualId)}
        onClick={() => call({ action: 'link', ...(needsManual ? { billing_id: Number(manualId) } : {}) })}
        className="inline-flex items-center gap-1 h-7 px-2 rounded bg-brand-600 text-white text-[11.5px] hover:bg-brand-700 disabled:opacity-40"
      >
        {busy ? <Loader2 size={11} className="animate-spin" /> : <Link2 size={11} />} ยืนยันและบันทึก
      </button>
      {err ? (
        <span className="inline-flex items-start gap-1 text-[11px] text-rose-700 max-w-[380px]">
          <AlertTriangle size={11} className="mt-0.5 shrink-0" />{err}
        </span>
      ) : null}
    </div>
  )
}
